import { ProviderMessageRequest, TemplateCreateRequest } from '../contracts/provider.contract';

/**
 * Base request adapter for transforming platform requests to provider-specific format
 */
export abstract class BaseRequestAdapter {
  /**
   * Transform a generic message request to provider-specific format
   */
  abstract transformMessageRequest(request: ProviderMessageRequest): unknown;

  /**
   * Transform a generic template request to provider-specific format
   */
  abstract transformTemplateRequest(request: TemplateCreateRequest): unknown;

  /**
   * Common transformation utilities
   */
  protected formatPhoneNumber(phoneNumber: string, countryCode: string = 'KR'): string {
    // Remove any non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    if (countryCode === 'KR') {
      // Korean phone number formatting
      if (digits.startsWith('82')) {
        // International format, remove country code
        return digits.substring(2);
      }
      
      if (digits.startsWith('0')) {
        // Domestic format with leading 0
        return digits;
      }
      
      // Add leading 0 if missing
      return '0' + digits;
    }
    
    return phoneNumber;
  }

  protected formatVariables(variables: Record<string, unknown>): Record<string, string> {
    const formatted: Record<string, string> = {};
    
    for (const [key, value] of Object.entries(variables)) {
      if (value instanceof Date) {
        formatted[key] = value.toISOString();
      } else if (typeof value === 'object') {
        formatted[key] = JSON.stringify(value);
      } else {
        formatted[key] = String(value);
      }
    }
    
    return formatted;
  }

  protected validateRequiredFields(data: unknown, requiredFields: string[]): void {
    const obj = data as Record<string, unknown>;
    for (const field of requiredFields) {
      if (!(field in obj) || obj[field] === undefined || obj[field] === null) {
        throw new Error(`Required field '${field}' is missing`);
      }
    }
  }
}

/**
 * Request adapter for IWINV provider
 */
export class IWINVRequestAdapter extends BaseRequestAdapter {
  transformMessageRequest(request: ProviderMessageRequest): unknown {
    this.validateRequiredFields(request, ['templateCode', 'phoneNumber']);

    return {
      profile_key: this.getProfileKey(),
      template_code: request.templateCode,
      phone_number: this.formatPhoneNumber(request.phoneNumber),
      message_variables: this.formatVariables(request.variables),
      sender_number: request.senderNumber,
      reserve_time: request.options?.scheduledAt ? 
        Math.floor(new Date(request.options.scheduledAt).getTime() / 1000) : undefined
    };
  }

  transformTemplateRequest(request: TemplateCreateRequest): unknown {
    this.validateRequiredFields(request, ['name', 'content']);

    return {
      profile_key: this.getProfileKey(),
      template_name: request.name,
      template_content: request.content,
      template_category: this.mapCategory(request.category),
      template_variables: request.variables?.map(v => ({
        name: v.name,
        type: v.type,
        required: v.required ? 'Y' : 'N',
        max_length: v.maxLength
      })),
      template_buttons: request.buttons?.map(b => ({
        type: b.type,
        name: b.name,
        url_mobile: b.linkMobile,
        url_pc: b.linkPc,
        scheme_ios: b.schemeIos,
        scheme_android: b.schemeAndroid
      }))
    };
  }

  private getProfileKey(): string {
    // This should come from configuration
    return process.env.IWINV_PROFILE_KEY || '';
  }

  private mapCategory(category: string): string {
    const categoryMap: Record<string, string> = {
      'AUTHENTICATION': 'A',
      'NOTIFICATION': 'N',
      'PROMOTION': 'P',
      'INFORMATION': 'I'
    };
    
    return categoryMap[category] || 'I';
  }
}

/**
 * Request adapter for Aligo provider
 */
export class AligoRequestAdapter extends BaseRequestAdapter {
  transformMessageRequest(request: ProviderMessageRequest): unknown {
    this.validateRequiredFields(request, ['templateCode', 'phoneNumber']);

    return {
      apikey: this.getApiKey(),
      userid: this.getUserId(),
      senderkey: this.getSenderKey(),
      template_code: request.templateCode,
      receiver: this.formatPhoneNumber(request.phoneNumber),
      subject: 'AlimTalk',
      message: this.buildMessage(request),
      button: request.variables.buttons ? JSON.stringify(request.variables.buttons) : undefined,
      reservation: request.options?.scheduledAt ? 
        this.formatDateTime(new Date(request.options.scheduledAt)) : undefined
    };
  }

  transformTemplateRequest(request: TemplateCreateRequest): unknown {
    this.validateRequiredFields(request, ['name', 'content']);

    return {
      apikey: this.getApiKey(),
      userid: this.getUserId(),
      senderkey: this.getSenderKey(),
      template_name: request.name,
      template_content: request.content,
      template_emphasis: this.extractEmphasis(request.content),
      template_extra: this.buildTemplateExtra(request),
      template_ad: this.isPromotional(request.category) ? 'Y' : 'N'
    };
  }

  private getApiKey(): string {
    return process.env.ALIGO_API_KEY || '';
  }

  private getUserId(): string {
    return process.env.ALIGO_USER_ID || '';
  }

  private getSenderKey(): string {
    return process.env.ALIGO_SENDER_KEY || '';
  }

  private buildMessage(request: ProviderMessageRequest): string {
    // This would typically involve replacing template variables
    // For now, return the template code as placeholder
    return request.templateCode;
  }

  private formatDateTime(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').replace('T', '').substring(0, 12);
  }

  private extractEmphasis(content: string): string {
    // Extract emphasized parts (bold, etc.) from content
    const emphasisMatch = content.match(/\*\*(.*?)\*\*/);
    return emphasisMatch ? emphasisMatch[1] : '';
  }

  private buildTemplateExtra(request: TemplateCreateRequest): string {
    const extra: Record<string, unknown> = {};
    
    if (request.buttons) {
      extra.buttons = request.buttons;
    }
    
    if (request.variables) {
      extra.variables = request.variables;
    }
    
    return JSON.stringify(extra);
  }

  private isPromotional(category: string): boolean {
    return category === 'PROMOTION';
  }
}

/**
 * Request adapter for Kakao provider (direct API)
 */
export class KakaoRequestAdapter extends BaseRequestAdapter {
  transformMessageRequest(request: ProviderMessageRequest): unknown {
    this.validateRequiredFields(request, ['templateCode', 'phoneNumber']);

    return {
      template_object: {
        object_type: 'text',
        text: this.buildTemplateText(request),
        link: this.buildTemplateLink(request),
        button_title: request.variables.buttonTitle || ''
      },
      user_ids: [this.formatPhoneNumber(request.phoneNumber)]
    };
  }

  transformTemplateRequest(request: TemplateCreateRequest): unknown {
    this.validateRequiredFields(request, ['name', 'content']);

    return {
      template: {
        name: request.name,
        content: request.content,
        category_code: this.mapCategoryCode(request.category),
        template_message_type: 'BA', // Basic AlimTalk
        template_emphasis_type: this.extractEmphasisType(request.content),
        template_title: request.name,
        template_subtitle: '',
        template_imageurl: '',
        template_header: '',
        template_item_highlight: {
          title: '',
          description: ''
        },
        template_item: {
          list: []
        },
        template_button: this.buildTemplateButtons(request.buttons || [])
      }
    };
  }

  private buildTemplateText(request: ProviderMessageRequest): string {
    // Build the actual message text by replacing variables
    let text = request.templateCode; // This should be the actual template content
    
    for (const [key, value] of Object.entries(request.variables)) {
      text = text.replace(new RegExp(`#{${key}}`, 'g'), String(value));
    }
    
    return text;
  }

  private buildTemplateLink(request: ProviderMessageRequest): unknown {
    if (request.variables.linkUrl) {
      return {
        web_url: request.variables.linkUrl,
        mobile_web_url: request.variables.linkUrl
      };
    }
    return {};
  }

  private mapCategoryCode(category: string): string {
    const categoryMap: Record<string, string> = {
      'AUTHENTICATION': '999999',
      'NOTIFICATION': '999998',
      'PROMOTION': '999997',
      'INFORMATION': '999996'
    };
    
    return categoryMap[category] || '999999';
  }

  private extractEmphasisType(content: string): string {
    if (content.includes('**')) return 'BOLD';
    if (content.includes('__')) return 'UNDERLINE';
    return 'NONE';
  }

  private buildTemplateButtons(buttons: unknown[]): unknown[] {
    return buttons.map(button => {
      const btn = button as Record<string, unknown>;
      return {
        name: btn.name,
        type: btn.type,
        url_mobile: btn.linkMobile,
        url_pc: btn.linkPc,
        scheme_ios: btn.schemeIos,
        scheme_android: btn.schemeAndroid
      };
    });
  }
}

/**
 * Factory for creating request adapters
 */
type RequestAdapterConstructor = new () => BaseRequestAdapter;

export class RequestAdapterFactory {
  private static adapters: Map<string, RequestAdapterConstructor> = new Map();

  static {
    RequestAdapterFactory.adapters.set('iwinv', IWINVRequestAdapter);
    RequestAdapterFactory.adapters.set('aligo', AligoRequestAdapter);
    RequestAdapterFactory.adapters.set('kakao', KakaoRequestAdapter);
  }

  static create(providerId: string): BaseRequestAdapter {
    const AdapterClass = this.adapters.get(providerId.toLowerCase());
    
    if (!AdapterClass) {
      throw new Error(`No request adapter found for provider: ${providerId}`);
    }
    
    return new AdapterClass();
  }

  static register(providerId: string, adapterClass: new () => BaseRequestAdapter): void {
    this.adapters.set(providerId.toLowerCase(), adapterClass);
  }
}